import React, { useState, useEffect, useCallback } from 'react';
import { authApi } from '../api';
import {
  AlertTriangle, Loader2, X, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// ─── LOSS BADGE ─────────────────────────────────────
const LossBadge = ({ pct }) => {
  if (pct > 5) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border bg-red-50 text-red-700 border-red-200">
        <AlertTriangle size={10} /> High Loss
      </span>
    );
  }
  if (pct >= 2) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border bg-orange-50 text-orange-700 border-orange-200">
        Monitor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border bg-emerald-50 text-emerald-700 border-emerald-200">
      Normal
    </span>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────
const MetalLossReport = () => {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await authApi.get('/inventory/reports/metal-loss', { params });
      setReport(res.data);
    } catch { setReport(null); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Chart data
  const chartData = report?.stages?.map(s => ({
    stage: s.stage_code,
    issued: s.total_issued_g,
    wastage: s.total_wastage_g,
    loss_pct: s.wastage_pct,
  })) || [];

  const hasHighLoss = report?.stages?.some(s => s.wastage_pct > 5);

  return (
    <div className="h-full flex flex-col bg-slate-50 text-slate-800">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle size={20} className="text-orange-600" /> Metal Loss Report
          </h1>
          {hasHighLoss && (
            <span className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-[10px] font-bold uppercase animate-pulse">
              High Loss Detected
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Track metal wastage per production stage across all job cards</p>
      </div>

      {/* Date Range Filter */}
      <div className="px-4 sm:px-6 py-3 border-b border-slate-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none" />
        </div>
        {(fromDate !== thirtyDaysAgo || toDate !== today) && (
          <button onClick={() => { setFromDate(thirtyDaysAgo); setToDate(today); }}
            className="px-3 py-2 text-xs text-slate-500 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-1">
            <X size={12} /> Reset
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-slate-500">
            <Loader2 size={24} className="animate-spin mr-2" /> Loading report...
          </div>
        ) : !report || !report.stages || report.stages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Activity size={40} className="mb-2 opacity-30" />
            <p className="text-sm">No wastage data for the selected period</p>
          </div>
        ) : (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Issued</p>
                <p className="text-lg font-bold text-slate-800 mt-0.5">{report.totals.total_issued_g.toFixed(3)}g</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Used</p>
                <p className="text-lg font-bold text-blue-600 mt-0.5">{report.totals.total_used_g.toFixed(3)}g</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Wastage</p>
                <p className="text-lg font-bold text-orange-600 mt-0.5">{report.totals.total_wastage_g.toFixed(3)}g</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Avg Loss %</p>
                <p className={`text-lg font-bold mt-0.5 ${report.totals.wastage_pct > 5 ? 'text-red-600' : report.totals.wastage_pct >= 2 ? 'text-orange-600' : 'text-emerald-600'}`}>
                  {report.totals.wastage_pct}%
                </p>
              </div>
            </div>

            {/* Bar Chart: Issued vs Wastage per Stage */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Issued vs Wastage by Stage</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="stage" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#CBD5E1' }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#CBD5E1' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A' }}
                      labelStyle={{ color: '#475569', fontWeight: 'bold' }}
                      formatter={(value, name) => [`${value.toFixed(3)}g`, name === 'issued' ? 'Issued' : 'Wastage']}
                    />
                    <Legend wrapperStyle={{ color: '#64748B', fontSize: 12 }} />
                    <Bar dataKey="issued" name="Issued (g)" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Bar dataKey="wastage" name="Wastage (g)" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Stage Table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Stage</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-blue-600 uppercase">Issued (g)</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Used (g)</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-orange-600 uppercase">Wastage (g)</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Loss %</th>
                      <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase">Flag</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Job Cards</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.stages.map((s, i) => (
                      <tr key={i} className="border-b border-slate-200 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{s.stage_code}</div>
                          <div className="text-[10px] text-slate-500">{s.stage_name}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-blue-600">{s.total_issued_g.toFixed(3)}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-500 hidden sm:table-cell">{s.total_used_g.toFixed(3)}</td>
                        <td className="px-4 py-3 text-right font-mono text-orange-600 font-bold">{s.total_wastage_g.toFixed(3)}</td>
                        <td className={`px-4 py-3 text-right font-mono font-bold ${s.wastage_pct > 5 ? 'text-red-600' : s.wastage_pct >= 2 ? 'text-orange-600' : 'text-emerald-600'}`}>
                          {s.wastage_pct}%
                        </td>
                        <td className="px-4 py-3 text-center"><LossBadge pct={s.wastage_pct} /></td>
                        <td className="px-4 py-3 text-right font-mono text-slate-500 hidden md:table-cell">{s.job_card_count}</td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot>
                    <tr className="bg-slate-50 border-t-2 border-slate-300">
                      <td className="px-4 py-3 font-bold text-slate-800 uppercase text-xs">Total</td>
                      <td className="px-4 py-3 text-right font-mono text-blue-600 font-bold">{report.totals.total_issued_g.toFixed(3)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500 font-bold hidden sm:table-cell">{report.totals.total_used_g.toFixed(3)}</td>
                      <td className="px-4 py-3 text-right font-mono text-orange-600 font-bold">{report.totals.total_wastage_g.toFixed(3)}</td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${report.totals.wastage_pct > 5 ? 'text-red-600' : report.totals.wastage_pct >= 2 ? 'text-orange-600' : 'text-emerald-600'}`}>
                        {report.totals.wastage_pct}%
                      </td>
                      <td className="px-4 py-3 text-center"><LossBadge pct={report.totals.wastage_pct} /></td>
                      <td className="px-4 py-3 hidden md:table-cell"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetalLossReport;
